import React, {useEffect, useRef, useState} from 'react';
import HandleHttpResponses from '../../components/NetworkErrorHandling';
import axios, {AxiosError} from 'axios';
import {NetworkCalls} from '../../NetworkCalls';
import {CatchYear} from '../../dtos/CatchYear';
import 'tom-select/dist/css/tom-select.bootstrap5.css';
import './EditCatchYear.css';
import TimestampFormat from '../../utils/TimestampFormat';
import ReportedCatchYears from './ReportedCatchYears';
import {Button, Dropdown} from 'react-bootstrap';
import DeleteDialog from '../../components/DeleteDialog';
import EmptyPage from '../../components/EmptyPage';
import AddEditCatchYear from './AddEditCatchYear';
import {Mode} from '../../dtos/Mode';
import {CatchYearCompletionPie} from '../../components/charts/CatchYearCompletionPie';
import {getData} from '../../utils/storage/Storage';
import {StorageKey} from '../../utils/storage/Keys';
import {useSearchParams} from 'react-router-dom';
import {useSelectedOrganisationContext} from "../../custom-hooks/useOrganisation";

export default function EditCatchYear() {
  const [loading, isLoading] = useState<boolean>(false);
  const [error, setError] = useState<AxiosError>();
  const [catchYear, setCatchYear] = useState<CatchYear[]>([]);
  const [selectedCatchYear, setSelectedCatchYear] = useState<
    CatchYear | undefined
  >(undefined);
  const [showDeleteDialog, setShowDeleteDialog] = useState<boolean>(false);
  const [showAddEditDialog, setShowAddEditDialog] = useState<boolean>(false);
  const [editMode, setEditMode] = useState<Mode>(Mode.ADD);
  const [searchParams, setSearchParams] = useSearchParams();
  const didApplyQuerySelection = useRef(false);

  // Daten für den Abschluss-Chart
  const [completedCount, setCompletedCount] = useState<number>(0);
  const [totalEligibleCount, setTotalEligibleCount] = useState<number | undefined>(undefined);

  const { selectedOrganisation } = useSelectedOrganisationContext();

  useEffect(() => {
    isLoading(true);
    NetworkCalls.getCatchYears()
      .then((result) => {
        isLoading(false);
        setCatchYear(result);
      })
      .catch((error) => {
        isLoading(false);
        setError(error);
      });
  }, []);

  useEffect(() => {
    if (didApplyQuerySelection.current || catchYear.length === 0) {
      return;
    }
    const catchYearId = searchParams.get('id');
    if (!catchYearId) {
      didApplyQuerySelection.current = true;
      return;
    }
    const match = catchYear.find((year) => year.id === catchYearId);
    if (match && match.id !== selectedCatchYear?.id) {
      setSelectedCatchYear(match);
    }
    didApplyQuerySelection.current = true;
  }, [catchYear, searchParams, selectedCatchYear]);

  useEffect(() => {
    const currentId = searchParams.get('id');
    const nextId = selectedCatchYear?.id ?? null;
    if (currentId === nextId) {
      return;
    }
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (selectedCatchYear?.id) {
        next.set('id', selectedCatchYear.id);
      } else {
        next.delete('id');
      }
      return next;
    }, {replace: true});
  }, [selectedCatchYear, setSearchParams, searchParams]);

  useEffect(() => {
    if (!selectedOrganisation) {
      return;
    }

    const fetchTotalEligible = async () => {
      try {

        const token = getData(StorageKey.TOKEN) as string;
        const res = await axios.get(
          `/organisation/${selectedOrganisation.id}/count-all-members`,
          {headers: {Authorization: 'Bearer ' + token}}
        );
        // API liefert Objekt mit totalMembers (siehe Dashboard). Wir lesen die Zahl explizit aus.
        const totalMembers =
          typeof res.data === 'object' && res.data !== null && 'totalMembers' in res.data
            ? (res.data as {totalMembers?: number}).totalMembers
            : undefined;
        setTotalEligibleCount(
          typeof totalMembers === 'number' && !isNaN(totalMembers) ? totalMembers : undefined
        );
      } catch (e) {
        setTotalEligibleCount(undefined);
      }
    };

    if (selectedCatchYear) {
      fetchTotalEligible().then();
    } else {
      setTotalEligibleCount(undefined);
    }
  }, [selectedCatchYear, selectedOrganisation]);


  function showContent() {
    return (
      <>
        <AddEditCatchYear
          onClose={(result) => {
            setShowAddEditDialog(false);
            if (result) {
              setCatchYear(result);
            }
          }}
          show={showAddEditDialog}
          editMode={editMode}
          selectedCatchYear={selectedCatchYear}
        />
        <div className="float-end w-50 m10">{alert()}</div>
        {!catchYear || catchYear.length === 0 ? (
          <div className="page-header d-print-none">
            <EmptyPage
              addNewElement={true}
              onItemAdd={() => {
                setEditMode(Mode.ADD);
                setShowAddEditDialog(true);
                setSelectedCatchYear(undefined);
              }}
            />
          </div>
        ) : (
          showData()
        )}
      </>
    );
  }

  const showData = () => {
    return (
      <>
        <div className="container-fluid px-0" style={{ height: 'calc(100vh - 120px)', overflow: 'hidden' }}>
          <div className="row g-0 h-100">
            <div
              className="col-12 col-md-3 bg-light border-end h-100 d-flex flex-column"
              style={{ overflow: 'hidden' }}
            >
              <div className="p-3 pb-0">
                <div className="d-flex align-items-center justify-content-between mb-3">
                  <h3 className="m-0">Fangjahre</h3>
                  <Button
                    onClick={() => {
                      setEditMode(Mode.ADD);
                      setShowAddEditDialog(true);
                      setSelectedCatchYear(undefined);
                    }}
                  >
                    Neues Fangjahr
                  </Button>
                </div>
              </div>
              <div className="flex-grow-1 px-3 pb-3" style={{ overflowY: 'auto', minHeight: 0 }}>
                <div className="list-group list-group-flush">
                  {getSortedCatchYears().map((year) => {
                    const isActive = selectedCatchYear?.id === year.id;
                    return (
                      <div
                        key={year.id ?? year.name}
                        className={`list-group-item list-group-item-action border-0 bg-transparent d-flex align-items-center justify-content-between px-2 catch-year-list-item${isActive ? ' active' : ''
                          }`}
                        role="button"
                        onClick={() => setSelectedCatchYear(year)}
                      >
                        <div className="me-2">
                          <div className="fw-semibold">{year.name}</div>
                          <div className="text-muted small">
                            {TimestampFormat.toDateOnlyReadableFormat(
                              year.startReport
                            )}{' '}
                            -{' '}
                            {TimestampFormat.toDateOnlyReadableFormat(
                              year.endReport
                            )}
                          </div>
                        </div>
                        <Dropdown
                          align="end"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Dropdown.Toggle
                            variant="link"
                            className="btn btn-link p-0 text-decoration-none text-primary"
                          >
                            ⋮
                          </Dropdown.Toggle>
                          <Dropdown.Menu
                            style={{ backgroundColor: "#fff", zIndex: 1060 }}
                          >
                            <Dropdown.Item
                              onClick={() => {
                                setEditMode(Mode.EDIT);
                                setSelectedCatchYear(year);
                                setShowAddEditDialog(true);
                              }}
                            >
                              Bearbeiten
                            </Dropdown.Item>
                            <Dropdown.Item
                              onClick={() => {
                                setShowDeleteDialog(true);
                                setSelectedCatchYear(year);
                              }}
                            >
                              Löschen
                            </Dropdown.Item>
                          </Dropdown.Menu>
                        </Dropdown>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            <div className="col-12 col-md-9 h-100" style={{ overflow: 'hidden' }}>
              <div className="p-3" style={{ height: 'calc(100% - 1.5rem)' }}>
                {selectedCatchYear ? (
                  <div className="row g-3 h-100">
                    <div className="col-12 col-lg-6 d-flex flex-column" style={{ maxHeight: '100%' }}>
                      <ReportedCatchYears
                        reportedCatchYear={selectedCatchYear}
                        onCountChange={setCompletedCount}
                      />
                    </div>
                    <div className="col-12 col-lg-6">
                      <CatchYearCompletionPie
                        completedCount={completedCount}
                        totalCount={totalEligibleCount}
                      />
                    </div>
                  </div>
                ) : (
                  <div className="d-flex align-items-center justify-content-center" style={{ height: "calc(100vh - 120px)" }}>
                    <EmptyPage
                      addNewElement={false}
                      text="Bitte wähle ein Fangjahr aus"
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <DeleteDialog
          onDeleteConfirm={async () => {
            setShowDeleteDialog(false);
            if (selectedCatchYear) {
              isLoading(true);
              setCatchYear(await NetworkCalls.deleteCatchYear(selectedCatchYear));
              isLoading(false);
            }
          }}
          title="Löschen"
          message="Datensatz wirklich löschen?"
          show={showDeleteDialog}
          onHandleClose={() => setShowDeleteDialog(false)}
        />
      </>
    );
  };

  const getSortedCatchYears = () => {
    return [...catchYear].sort(
      (a, b) => b.startCatchYear - a.startCatchYear
    );
  };

  const loadingPage = () => {
    return (
      <div className="page-header d-print-none">
        <div className="container-xl">
          <div className="row row-cards">
            <div className="col-12">
              <div className="card">
                <ul className="list-group list-group-flush placeholder-glow">
                  <li className="list-group-item">
                    <div className="row align-items-center">
                      <div className="col-auto">
                        <div className="avatar avatar-rounded placeholder"></div>
                      </div>
                      <div className="col-7">
                        <div className="placeholder placeholder-xs col-9"></div>
                        <div className="placeholder placeholder-xs col-7"></div>
                      </div>
                      <div className="col-2 ms-auto text-end">
                        <div className="placeholder placeholder-xs col-8"></div>
                        <div className="placeholder placeholder-xs col-10"></div>
                      </div>
                    </div>
                  </li>
                  <li className="list-group-item">
                    <div className="row align-items-center">
                      <div className="col-auto">
                        <div className="avatar avatar-rounded placeholder"></div>
                      </div>
                      <div className="col-7">
                        <div className="placeholder placeholder-xs col-9"></div>
                        <div className="placeholder placeholder-xs col-7"></div>
                      </div>
                      <div className="col-2 ms-auto text-end">
                        <div className="placeholder placeholder-xs col-8"></div>
                        <div className="placeholder placeholder-xs col-10"></div>
                      </div>
                    </div>
                  </li>
                  <li className="list-group-item">
                    <div className="row align-items-center">
                      <div className="col-auto">
                        <div className="avatar avatar-rounded placeholder"></div>
                      </div>
                      <div className="col-7">
                        <div className="placeholder placeholder-xs col-9"></div>
                        <div className="placeholder placeholder-xs col-7"></div>
                      </div>
                      <div className="col-2 ms-auto text-end">
                        <div className="placeholder placeholder-xs col-8"></div>
                        <div className="placeholder placeholder-xs col-10"></div>
                      </div>
                    </div>
                  </li>
                  <li className="list-group-item">
                    <div className="row align-items-center">
                      <div className="col-auto">
                        <div className="avatar avatar-rounded placeholder"></div>
                      </div>
                      <div className="col-7">
                        <div className="placeholder placeholder-xs col-9"></div>
                        <div className="placeholder placeholder-xs col-7"></div>
                      </div>
                      <div className="col-2 ms-auto text-end">
                        <div className="placeholder placeholder-xs col-8"></div>
                        <div className="placeholder placeholder-xs col-10"></div>
                      </div>
                    </div>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const alert = () => {
    return (
      <div
        className={
          !error
            ? 'alert alert-danger alert-dismissible d-none'
            : 'alert alert-danger alert-dismissible'
        }
        role="alert"
        id="alert"
      >
        <div className="d-flex">
          <div>
            <h4 className="alert-title">Problem</h4>
            <div className="text-muted">
              Da ist etwas schief gelaufen. Versuche es nochmal oder kontaktiere
              uns
            </div>
          </div>
        </div>
        <button
          className="btn-close"
          data-bs-dismiss="alert"
          aria-label="close"
        ></button>
      </div>
    );
  };

  return (
    <div>
      {loading
        ? loadingPage()
        : error
          ? HandleHttpResponses(error)
          : showContent()}
    </div>
  );
}
